import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAlerts } from "common";
import { BundleIds, OnboardCandidateStatus } from "app/shared/constants";
import { useApp } from "app/appService";
import { clearAuthStorage } from "app/portal/portalAuthService";
import isJSON from "is-json";

const FormsContext = createContext();

export const FormsProvider = ({ apiUrl, children }) => {
  const [formFields, setFormFields] = useState([]);
  const [allFields, setAllFields] = useState([]);
  const [summaryFields, setSummaryFields] = useState([]);
  const [formAccess, setFormAccess] = useState();
  const [formFlatMap, setFormFlatMap] = useState({});
  const [formLabelMap, setFormLabelMap] = useState({});
  const [formValues, setFormValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [duplicateIds, setDuplicateIds] = useState([]);
  const [groupedFieldIds, setGroupedFieldIds] = useState([]);
  const [listGroupedSections, setListGroupedSections] = useState([]);
  const [sectionsReferenceList, setSectionsReferenceList] = useState([]);
  const [navigableSections, setNavigableSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [hasFormErrors, setHasFormErrors] = useState(false);
  const [summaryFormSections, setSummaryFormSections] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const { clearAlert, setAlert } = useAlerts();
  const [showStartHere, setShowStartHere] = useState(false);
  const { bundleId } = useApp();

  const isNavigationPanelShown = navigableSections.length > 0;

  // Function to re-structure the summary form sections to include the following fields
  const structureSummaryFormSections = (fieldsList) => {
    if (!fieldsList || fieldsList.length === 0) {
      return setSummaryFormSections([]);
    }

    const result = [];
    let currentSection = null;

    for (const field of fieldsList) {
      const fieldAttributes = isJSON(field.field_attributes)
        ? JSON.parse(field.field_attributes)
        : {};

      if (fieldAttributes.field_type === "section") {
        // Start a new section
        const section = { ...field, fields: [] };
        result.push(section);
        currentSection = section;
      } else {
        // Append to the current section
        currentSection.fields.push(field);
      }
    }

    // Filter sections based on child field values or "alwaysShow" attribute
    const filteredResult = result.filter((section) => {
      const sectionAttributes = isJSON(section.field_attributes)
        ? JSON.parse(section.field_attributes)
        : {};

      return (
        sectionAttributes.alwaysShow ||
        section.fields.some(
          (field) =>
            field.field_value ||
            field.field_value === 0 ||
            field.field_value === false ||
            (typeof field.field_value === "string" &&
              field.field_value.trim() !== ""),
        )
      );
    });

    setSummaryFormSections(filteredResult);
  };

  // Function to update sections reference list
  const updateSectionsReferenceList = (formData) => {
    // Create an array of all the section fields
    const sectionFields = formData.filter((field) => {
      const fieldAttributes = isJSON(field.field_attributes)
        ? JSON.parse(field.field_attributes)
        : {};
      return (
        fieldAttributes.field_type === "section" ||
        field.field_type === "section"
      );
    });
    // Create an index map of section fields with their labels and order
    const sectionFieldsIndexMap = new Map(
      sectionFields.map((field, index) => [field.field_label, index]),
    );
    // Update the sections reference list with the index map
    setSectionsReferenceList(sectionFieldsIndexMap);
  };

  const formName = apiUrl && apiUrl.split("/").pop();

  useEffect(() => {
    setFormFields([]);
    setAllFields([]);
    setSummaryFields([]);
    setFormFlatMap({});
    setFormLabelMap({});
    setFormValues({});
    setListGroupedSections([]);
    setGroupedFieldIds([]);
    setNavigableSections([]);
    setSectionsReferenceList([]);
  }, [formName]);

  useEffect(() => {
    (async () => {
      clearAlert();
      setIsLoading(true);

      try {
        if (apiUrl) {
          const response = await axios(apiUrl);
          response.data.fields.forEach((item, index) => {
            if (item.field_value === "{}" || item.field_value === null) {
              response.data.fields[index].field_value = "";
            }
          });
          const formData = response.data.fields;
          updateSectionsReferenceList(formData);

          const fields = getFormFields(response);

          setAllFields(formData);

          setFormAccess(response.data.access);
          setFormFields(fields);

          const flatMap = flatten({}, formData);
          const labelMap = getLabels(formData);
          const values = flatten({}, getUserFields(formData));

          setFormFlatMap(flatMap);
          setFormLabelMap(labelMap);
          setFormValues(values);
          setDuplicateIds(getDuplicateIds(formData));
          const groupedSections = formatSections(
            convertListToGroupSections(fields),
          );
          setListGroupedSections(groupedSections);
          setGroupedFieldIds(getGroupedIds(groupedSections));

          const summaryRows = formData.filter((item) => getSummaryFields(item));

          structureSummaryFormSections(summaryRows);

          const candidateStatus = formData.find(
            (f) => f.field_id === "candidate_status",
          );

          setSummaryFields(summaryRows);
          setShowStartHere(
            candidateStatus?.field_value === OnboardCandidateStatus.INVITED,
          );
          setIsLoading(false);
        }
      } catch (error) {
        if (
          error?.response?.status === 401 &&
          bundleId === BundleIds.MEDONBOARD_PORTAL
        ) {
          clearAuthStorage();
          window.location.href = "/";
        }
        console.log(error);
        setIsLoading(false);
        setAlert("error", error.message);
      }
    })();
  }, [apiUrl, bundleId, clearAlert, formName, setAlert]);

  return (
    <FormsContext.Provider
      value={{
        formFields,
        formFlatMap,
        formAccess,
        formValues,
        isLoading,
        showStartHere,
        allFields,
        summaryFields,
        duplicateIds,
        formLabelMap,
        groupedFieldIds,
        listGroupedSections,
        activeSection,
        setActiveSection,
        navigableSections,
        setNavigableSections,
        sectionsReferenceList,
        summaryFormSections,
        isNavigationPanelShown,
        optionsLoading,
        setOptionsLoading,
        hasFormErrors,
        setHasFormErrors,
      }}
    >
      {children}
    </FormsContext.Provider>
  );
};

// Custom hook
export const useForms = () => {
  return useContext(FormsContext);
};

const flatten = (into, node) => {
  if (node == null) return into;
  if (Array.isArray(node)) return node.reduce(flatten, into);
  into[node.field_id] = node && !isImmutable(node) ? node.field_value : "";
  return flatten(into, node?.subOrgs);
};

const getLabels = (formData) => {
  const labelMap = {};
  for (let i of formData) {
    labelMap[i.field_id] = i.field_label;
  }
  return labelMap;
};

export const isHidden = (field) =>
  // Currently two condition is being maintained to make the backward compatiblity with other tenants.
  // Once other tenants update their formspec, then condition of @HIDDEN can be removed.
  field["field_type"] === "hidden" ||
  field?.["field_format"]?.split(" ")?.indexOf("@HIDDEN") > -1;

export const isImmutable = (element) => {
  const clearOnLoad = isJSON(element.field_attributes)
    ? JSON.parse(element.field_attributes)["clearOnLoad"]
    : "";
  // Currently two condition is being maintained to make the backward compatiblity with other tenants.
  // Once other tenants update their formspec, then condition of @IMMUTABLE can be removed.
  return (
    clearOnLoad === true ||
    element?.["field_format"]?.split(" ")?.indexOf("@IMMUTABLE") > -1
  );
};

export const isUserField = (field) =>
  [
    "text",
    "number",
    "money",
    "percent",
    "radio",
    "dropdown",
    "checkbox",
  ].includes(field.field_type);

export const getUserFields = (fields) =>
  fields.filter((field) => isUserField(field));

export const getDuplicateIds = (fields) => {
  const lookup = fields.reduce((a, e) => {
    a[e.field_id] = ++a[e.field_id] || 0;
    return a;
  }, {});

  return [
    ...new Set(
      fields.filter((e) => lookup[e.field_id]).map((item) => item.field_id),
    ),
  ];
};

export const getSummaryFields = (element) =>
  element["field_type"] === "summary";

const getFormFields = (response) => {
  let summaryAdded = false;
  return response.data.fields.filter((item) => {
    if (
      (item.field_type === "summary" && !summaryAdded) ||
      item.field_type !== "summary"
    ) {
      if (item.field_type === "summary") summaryAdded = true;
      return item;
    }
    return false;
  });
};

/**
 * getGroupedIds function will helps us to create a list of all the fields that needs to be grouped for the Accordion type section.
 *
 * @param {Array} fields The values from the formspec
 * @returns It will return a list of all the grouped fields
 */
const getGroupedIds = (fields) => {
  let groupIds = [];
  for (const element in fields) {
    groupIds.push(...fields[element]);
  }
  return groupIds;
};

/**
 * convertListToGroupSections function will helps us to create a list of all the section and their respective fields that needs to be grouped.
 *
 * @param {Array} fields The values from the formspec
 * @returns It will return a list of all the section and their respective grouped fields
 */
const convertListToGroupSections = (fields) => {
  return fields.reduce(
    (acc, field) => {
      if (field.field_type === "section") {
        const sectionName = field.field_id;
        acc.currentSection = sectionName;
        acc[sectionName] = [];
      } else if (acc.currentSection) {
        if (
          field.field_type !== "hidden" &&
          field.field_type !== "button" &&
          field.field_type !== "errors"
        ) {
          const currentList = acc[acc.currentSection];
          if (!currentList.includes(field.field_id)) {
            currentList.push(field.field_id);
          }
        }
      } else {
        // console.warn("Field before any section:", field);
      }
      return acc;
    },
    { currentSection: null },
  );
};

const formatSections = (sections) => {
  const { currentSection, ...cleaned } = sections;
  return cleaned;
};
